import * as Header from '-/Custom/Header';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import { v4 } from 'uuid';
import FadeInContent from '@/components/Animation/FadeInContent';
import LegendList from '@/components/Custom/LegendList';
import * as Placeholder from '@/components/Custom/Placeholder';
import type { EventEditFormValues } from '@/components/Event/EventEdit.types';
import Badge from '@/components/UI/Badge';
import Box from '@/components/UI/Box';
import Button from '@/components/UI/Button';
import Row from '@/components/UI/Row';
import Text from '@/components/UI/Text';
import DepthListItem from '@/components/Unistyles/DepthListItem';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import { graphql, type ResultOf } from '@/gql/graphql';
import eventTypeMap from '@/utils/eventTypeMap';
import getEmptyEntity from '@/utils/getEmptyEntity';
import getRandomColor from '@/utils/getRandomColor';

const GetEventEditEvent = graphql(/* GraphQL */ `
  query GetEventEditEvent($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      parentId: parent_id
      rootParentId: root_parent_id
    }
  }
`);
const GetEventEditParent = graphql(/* GraphQL */ `
  query GetEventEditParent($parentId: uuid!) {
    event_by_pk(id: $parentId) {
      id
      title
    }
  }
`);
const GetEventEditParentEvents = graphql(/* GraphQL */ `
  query GetEventEditParentEvents($where: event_bool_exp!) {
    event(where: $where, order_by: { ancestry: { title: asc } }, limit: 100) {
      id
      title
      rootParentId: root_parent_id
      parentId: parent_id
      tagGroup: tag_group {
        id
        title
      }
    }
  }
`);
type EventEditParentItem = ResultOf<
  typeof GetEventEditParentEvents
>['event'][number] & {
  isNew?: boolean;
};
export default function EventEditParent() {
  const { eventId, initial, generate } = useLocalSearchParams<{
    eventId?: string;
    initial?: string;
    generate?: string;
  }>();
  const { setValue, getValues, reset } = useFormContext<EventEditFormValues>();
  const { title, parentId, eventType } = useWatch<EventEditFormValues>();
  const { replace } = useRouter();
  const [{ data: eventData }] = useQuery({
    query: GetEventEditEvent,
    variables: {
      eventId: eventId || '',
    },
    pause: !eventId,
    requestPolicy: 'cache-and-network',
  });
  const [{ data: parentData }] = useQuery({
    query: GetEventEditParent,
    variables: {
      parentId: parentId || '',
    },
    pause: !parentId,
    requestPolicy: 'cache-and-network',
  });
  const [lastRootParentId, setLastRootParentId] = useState(
    eventData?.event_by_pk?.rootParentId,
  );
  const eventTypeLabel = eventTypeMap[eventType || 'game'].label;
  const [{ data: eventGroupData, fetching }] = useQuery({
    query: GetEventEditParentEvents,
    variables: {
      where: {
        permissions: {
          _has_key: 'edit',
        },
        is_group: {
          _eq: true,
        },
        ...(eventId
          ? {
              ancestry: {
                _not: {
                  ancestor_ids: {
                    _contains: [eventId],
                  },
                },
              },
            }
          : {}),
        ...(eventId && eventData?.event_by_pk?.parentId
          ? {
              root_parent_id: {
                _eq: eventData?.event_by_pk.rootParentId,
              },
            }
          : {}),
      },
    },
    requestPolicy: 'cache-and-network',
  });
  const combinedEvents =
    eventGroupData?.event.reduce((acc, event) => {
      acc.push(event);
      if (event.id === parentId) {
        acc.push({
          id: eventId || v4(),
          title: title || `New ${eventTypeLabel}`,
          parentId: event.id,
          tagGroup: null,
          rootParentId: event.rootParentId,
          isNew: true,
        });
      }
      return acc;
    }, [] as EventEditParentItem[]) || [];
  const onPressItem = (eventGroup: EventEditParentItem) => {
    if (initial) {
      replace({
        pathname: '/event/create',
        params: {
          eventType: 'group',
          parentId: eventGroup.parentId,
        },
      });
      setValue('parentId', eventGroup.id, {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }
    if (generate) {
      replace({
        pathname: '/event/[eventId]/generate',
        params: {
          eventId: eventGroup.id,
        },
      });
      return;
    }
    setTimeout(() => {
      setValue('parentId', eventGroup.id, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }, 0);
    if (lastRootParentId !== eventGroup.rootParentId) {
      setLastRootParentId(eventGroup.rootParentId);
      setValue(
        'teams',
        getValues('teams')?.map((team) =>
          team.isNew ? team : getEmptyEntity(getRandomColor()),
        ),
      );
    }
  };
  const listItem = (args: { item: EventEditParentItem; index: number }) => {
    return (
      <Pressable
        onPress={() => onPressItem(args.item)}
        disabled={args.item.isNew}
        style={style.row}
      >
        <DepthListItem
          items={combinedEvents}
          index={args.index}
          getId={(event) => event.id}
          getParentId={(event) => event.parentId}
        >
          <Box direction="horizontal" alignItems="center" space="md">
            <Text
              flexShrink={1}
              numberOfLines={1}
              size="lg"
              variant={args.item.isNew ? 'primary' : undefined}
            >
              {args.item.title}
            </Text>
            {args.item.tagGroup?.title ? (
              <Badge>
                <Badge.Text>{args.item.tagGroup.title}</Badge.Text>
              </Badge>
            ) : null}
          </Box>
        </DepthListItem>
      </Pressable>
    );
  };
  return (
    <FadeInContent fetching={fetching}>
      <Stack.Screen
        options={{
          headerTitle: '',
        }}
      />

      <Box flex>
        <LegendList
          data={combinedEvents}
          renderItem={listItem}
          keyExtractor={(item) => item.id}
          style={style.list}
          ListHeaderComponent={
            combinedEvents.length ? (
              <Row>
                <Text italic bold flexShrink={1} size="md" variant="primary">
                  {parentId && parentData?.event_by_pk?.title
                    ? `Grouped Under ${parentData.event_by_pk.title}`
                    : 'Choose an Organization, League, or Season for this to be grouped under.'}
                </Text>

                {parentId && !eventId ? (
                  <Header.Button
                    onPress={() =>
                      setValue('parentId', null, {
                        shouldValidate: true,
                        shouldTouch: true,
                      })
                    }
                  >
                    <Icon name="close-outline" />
                  </Header.Button>
                ) : null}
              </Row>
            ) : null
          }
          ListEmptyComponent={
            <Placeholder.Container>
              <Placeholder.Icon name="globe-outline" />
              <Placeholder.Text>
                You must be part of an organization to create a League or
                Season. Create an organization now?
              </Placeholder.Text>
              <Button
                onPress={() => {
                  replace({
                    pathname: '/event/create',
                  });
                  reset(
                    {
                      eventType: 'organization',
                    },
                    {
                      keepDefaultValues: true,
                    },
                  );
                }}
                appearance="outline"
              >
                <Button.Text>Create Organization</Button.Text>
              </Button>
            </Placeholder.Container>
          }
        />
      </Box>
    </FadeInContent>
  );
}
const style = StyleSheet.create({
  list: {
    flex: 1,
  },
  row: {
    height: 48,
    paddingHorizontal: 0,
    paddingVertical: 0,
    overflow: 'hidden',
    opacity: 1,
  },
});
